/**
 * Utility functions for generating correlation IDs
 */

// declare all constants across application
export const PAST_MONTH_DATA = 36;

/**
 * Helper function to get current month in YYYYMM format
 * @returns Current month string in format "YYYYMM" (e.g., "2026M01")
 */
export const getCurrentMonth = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  return `${year}M${month}`;
};

/**
 * Helper function to generate correlation ID as GUID
 * @returns A unique correlation ID string
 */
export const generateCorrelationId = (): string => {
  return crypto.randomUUID();
};

export const convertToBoolean = (value: unknown): boolean | null => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    const toBooleanOrNull = (value: string) => {
      if (value === null) {
        return null;
      }
      if (value === "true") {
        return true;
      }
      if (value === "false") {
        return false;
      }
      return null;
    };

    return toBooleanOrNull(value);
  }
  return null;
};

/**
 * Helper function to get current UTC time in ISO format
 * @returns Current UTC time string in ISO format (e.g., "2025-12-02T07:04:07.321Z")
 */
export const getCurrentUtcTime = (): string => {
  return new Date().toISOString();
};

/**
 * Helper function to format date to yyyy-mm-dd format
 * @param dateString - Date string in any valid format (ISO, DD-MM-YYYY, YYYYMMDD, etc.)
 * @returns Date string in yyyy-mm-dd format or original string if invalid
 * @example
 * formatDateToYYYYMMDD("22-01-2025 03:56:39") // "2025-01-22"
 * formatDateToYYYYMMDD("2025-01-22T10:30:00.000Z") // "2025-01-22"
 * formatDateToYYYYMMDD("22-01-2025") // "2025-01-22"
 * formatDateToYYYYMMDD("20250428") // "2025-04-28"
 */
export const formatDateToYYYYMMDD = (dateString: string): string => {
  if (!dateString) return "";

  try {
    // Handle YYYYMMDD format (8 digits: YYYYMMDD)
    if (/^\d{8}$/.test(dateString)) {
      const year = dateString.slice(0, 4);
      const month = dateString.slice(4, 6);
      const day = dateString.slice(6, 8);
      return `${year}-${month}-${day}`;
    }

    // Handle DD-MM-YYYY format (convert to YYYY-MM-DD)
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split("-");
      return `${year}-${month}-${day}`;
    }

    // Handle YYYY-MM-DD format (already in target format)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    // Handle DD-MM-YYYY HH:MM:SS format - extract and convert just the date part
    if (/^\d{2}-\d{2}-\d{4}\s\d{2}:\d{2}:\d{2}$/.test(dateString)) {
      const datePart = dateString.split(" ")[0];
      const [day, month, year] = datePart.split("-");
      return `${year}-${month}-${day}`;
    }

    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString;
    }

    // Handle ISO 8601 format with Z (UTC) - use UTC methods to avoid timezone conversion
    if (dateString.includes("T") && dateString.includes("Z")) {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const day = String(date.getUTCDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    }

    // For general date strings, use local timezone methods
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString;
  }
};

/**
 * Helper function to format date to dd-mm-yyyy format
 * @param dateString - Date string in any valid format (ISO, YYYY-MM-DD, etc.)
 * @returns Date string in dd-mm-yyyy format or original string if invalid
 * @example
 * formatDateToDDMMYYYY("22-01-2025 03:56:39") // "22-01-2025"
 * formatDateToDDMMYYYY("2025-01-22T10:30:00.000Z") // "22-01-2025"
 * formatDateToDDMMYYYY("2025-01-22") // "22-01-2025"
 */
export const formatDateToDDMMYYYY = (dateString: string): string => {
  if (!dateString) return "";

  try {
    // Handle DD-MM-YYYY HH:MM:SS format - extract just the date part
    if (dateString.includes(" ") && dateString.split("-").length === 3) {
      const datePart = dateString.split(" ")[0];
      // Validate DD-MM-YYYY format
      const parts = datePart.split("-");
      if (
        parts.length === 3 &&
        parts[0].length === 2 &&
        parts[1].length === 2 &&
        parts[2].length === 4
      ) {
        return datePart;
      }
    }

    // Handle DD-MM-YYYY format (already in target format)
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      return dateString;
    }

    // Handle YYYY-MM-DD format (parse components to avoid timezone issues)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split("-");
      return `${day}-${month}-${year}`;
    }

    // Handle ISO 8601 format with Z (UTC) - use UTC methods to avoid timezone conversion
    if (dateString.includes("T") && dateString.includes("Z")) {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }

      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const day = String(date.getUTCDate()).padStart(2, "0");

      return `${day}-${month}-${year}`;
    }

    // Handle other date strings - parse and format carefully
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString;
    }

    // For general date strings, use local timezone methods
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${day}-${month}-${year}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString;
  }
};

/**
 * Helper function to convert date from DD-MM-YYYY format to ISO 8601 format
 * @param dateString - Date string in DD-MM-YYYY format
 * @returns ISO 8601 formatted date string (e.g., "2025-12-02T00:00:00.000Z")
 */
export const convertToISODate = (dateString: string): string => {
  // Handle empty or invalid strings
  if (!dateString || typeof dateString !== "string") {
    return new Date().toISOString();
  }

  // If already in ISO format, return as is
  if (dateString.includes("T") && dateString.includes("Z")) {
    return dateString;
  }

  // Parse DD-MM-YYYY format
  const parts = dateString.split("-");
  if (parts.length === 3) {
    const [day, month, year] = parts;
    // Create date in UTC (month is 0-indexed in JavaScript)
    const date = new Date(
      Date.UTC(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10)),
    );
    return date.toISOString();
  }

  // If format doesn't match, try to parse as-is
  const date = new Date(dateString);
  return date.toISOString();
};

/**
 * Helper function to generate past months in yyyyMmm format
 * @param numberOfMonths - Number of past months to generate (default: 36 for 3 years)
 * @returns Array of month objects with value and label in yyyyMmm format
 */
export const generatePastMonthOptions = (
  numberOfMonths: number = 36,
): Array<{ value: string; label: string }> => {
  const options = [];
  const today = new Date();

  for (let i = 0; i < numberOfMonths; i++) {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const value = `${year}M${month}`;
    options.push({
      value: value,
      label: value,
    });
  }

  return options;
};

/**
 * Helper function to generate all dates for a specific month from yyyyMmm format
 * @param monthString - Month string in yyyyMmm format (e.g., "2025M12")
 * @returns Array of date objects with value (ISO date string) and label (formatted date)
 */
export const generateDatesForMonth = (
  monthString: string,
): Array<{ value: string; label: string }> => {
  if (!monthString) return [];

  // Parse the month string (e.g., "2025M12")
  const parts = monthString.split("M");
  if (parts.length !== 2) return [];

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-indexed

  if (isNaN(year) || isNaN(month) || month < 0 || month > 11) return [];

  const dates = [];
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  for (let day = 1; day <= daysInMonth; day++) {
    // Construct the ISO date string directly to avoid timezone issues
    const monthStr = String(month + 1).padStart(2, "0");
    const dayStr = String(day).padStart(2, "0");
    const isoDate = `${year}-${monthStr}-${dayStr}`; // "YYYY-MM-DD"

    // Create date for formatting (use local date to match the ISO date)
    const date = new Date(year, month, day);
    const formattedDate = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }); // "Dec 1, 2025"

    dates.push({
      value: isoDate,
      label: formattedDate,
    });
  }

  return dates;
};

/**
 * Helper function to get tomorrow's date
 * @returns Date object representing tomorrow
 */
export const getTomorrowDate = (): Date => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow;
};

/**
 * Helper function to format date as YYYY-MM-DD using local timezone
 * @param date - Date object to format
 * @returns Date string in YYYY-MM-DD format
 */
export const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
